import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';

// Topic-specific icons mapping
const getTopicIcon = (title: string): string => {
  const lowerTitle = title.toLowerCase();
  
  if (lowerTitle.includes('روانشناس') || lowerTitle.includes('psychology')) return '🧠';
  if (lowerTitle.includes('مدیریت') || lowerTitle.includes('کسب') || lowerTitle.includes('کار') || lowerTitle.includes('business')) return '💼';
  if (lowerTitle.includes('اقتصاد') || lowerTitle.includes('مالی') || lowerTitle.includes('finance') || lowerTitle.includes('economy')) return '💰';
  if (lowerTitle.includes('ارتباط') || lowerTitle.includes('communication')) return '💬';
  if (lowerTitle.includes('فلسفه') || lowerTitle.includes('philosophy')) return '🤔';
  if (lowerTitle.includes('تاریخ') || lowerTitle.includes('history')) return '📜';
  if (lowerTitle.includes('علم') || lowerTitle.includes('science')) return '🔬';
  if (lowerTitle.includes('فناوری') || lowerTitle.includes('تکنولوژی') || lowerTitle.includes('technology')) return '💻';
  if (lowerTitle.includes('هنر') || lowerTitle.includes('art')) return '🎨';
  if (lowerTitle.includes('ادبیات') || lowerTitle.includes('literature')) return '📖';
  if (lowerTitle.includes('سلامت') || lowerTitle.includes('health')) return '🏥';
  if (lowerTitle.includes('ورزش') || lowerTitle.includes('sport')) return '⚽';
  
  return '📚'; // Default book icon
};

interface SelectionCardProps {
  title: string;
  description?: string; // Made optional for backward compatibility
  icon?: string; // Made optional for backward compatibility
  isSelected?: boolean;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

const SelectionCard = ({
  title,
  description,
  icon,
  isSelected = false,
  onClick,
  className,
  disabled = false
}: SelectionCardProps) => {

  return (
    <motion.div
      className={cn(
        'relative p-3 rounded-3xl border-2 cursor-pointer',
        'bg-white transition-all duration-300',
        'select-none',
        'min-h-[68px] w-full',
        'flex flex-row items-center',

        // Default state
        !isSelected && !disabled && [
          'border-gray-200 hover:border-primary-300',
          'shadow-sm hover:shadow-md'
        ],

        // Selected state - enhanced with glow and primary border
        isSelected && [
          'border-primary-500',
          'shadow-lg',
          'bg-primary-50/30'
        ],

        // Disabled state
        disabled && [
          'border-gray-100 bg-gray-50',
          'cursor-not-allowed opacity-60'
        ],

        className
      )}
      onClick={disabled ? undefined : onClick}
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
    >
      {/* Glow effect for selected state */}
      {isSelected && (
        <div
          className="absolute inset-0 -z-10 rounded-3xl"
          style={{
            background: 'radial-gradient(closest-side, rgba(112,99,255,0.15), rgba(112,99,255,0) 70%)',
            filter: 'blur(8px)'
          }}
        />
      )}

      {/* Icon - left side with topic-specific icons */}
      <div className="text-2xl mr-4 flex-shrink-0">
        {icon || getTopicIcon(title)}
      </div>

      {/* Content - right side */}
      <div className="flex-1 text-right">
        {/* Title */}
        <div className="mb-1">
          <span className={cn(
            'text-base font-medium font-persian',
            isSelected ? 'text-primary-700' : 'text-foreground'
          )}>
            {title}
          </span>
        </div>

        {/* Description */}
        {description && (
          <p className={cn(
            'text-sm text-right leading-tight',
            'font-persian',
            isSelected ? 'text-primary-600' : 'text-muted'
          )}>
            {description}
          </p>
        )}
      </div>

    </motion.div>
  );
};

export default SelectionCard;
